import type {FC} from 'react';
import {CheckCircleIcon} from '@heroicons/react/20/solid';
import type {PortableTextMarkComponentProps} from '@portabletext/react';
import {PortableText as PortableTextReact} from '@portabletext/react';
import type {PortableTextBlock} from '@portabletext/types';
import clsx from 'clsx';
import type {SetOptional} from 'type-fest';

import {CallToActionLine} from 'components/call-to-action-line';
import type {CallToActionLineSchema} from 'components/call-to-action-line/schema';
import {InlineLink} from 'components/inline-link';
import type {LinkSchema} from 'components/inline-link/schema';

interface Props {
	blocks: SetOptional<PortableTextBlock, 'children'>[];
	className?: string;
	isDraft: boolean;
}

export const PortableText: FC<Props> = ({blocks, className, isDraft}) => {
	const headingColor = 'text-primary-main';

	return (
		<PortableTextReact
			value={blocks}
			components={{
				block: {
					normal: ({children}) => {
						return (
							<p className={clsx('mb-6', 'last:mb-0')}>
								<span className={clsx(className, 'inline-block', 'empty:h-6')}>
									{children}
								</span>
							</p>
						);
					},
					blockquote: ({children}) => (
						<blockquote
							className={clsx(
								'my-10',
								'border-l-4',
								'border-primary-light',
								'pl-9',
								'font-semibold',
								'text-gray-900',
							)}
						>
							<p>
								<span className="text-secondary">“</span>
								{children}
								<span className="text-secondary">”</span>
							</p>
						</blockquote>
					),
					bigText: ({children}) => {
						return (
							<p className={clsx('text-4xl', 'font-bold')}>
								<span className={clsx(className, 'inline-block', 'empty:h-6')}>
									{children}
								</span>
							</p>
						);
					},
					footnote: ({children}) => {
						return (
							<p className={clsx('text-xs')}>
								<span className={clsx(className, 'inline-block', 'empty:h-6')}>
									{children}
								</span>
							</p>
						);
					},
					h2: ({children}) => (
						<h2
							className={clsx(
								'text-2xl',
								'font-bold',
								'md:text-3xl',
								headingColor,
								'mb-4',
							)}
						>
							{children}
						</h2>
					),
					h3: ({children}) => (
						<h3
							className={clsx(
								'text-xl',
								'font-bold',
								'md:text-2xl',
								headingColor,
								'mb-3',
							)}
						>
							{children}
						</h3>
					),
					h4: ({children}) => (
						<h4
							className={clsx(
								'text-lg',
								'font-bold',
								'md:text-xl',
								headingColor,
								'mb-3',
							)}
						>
							{children}
						</h4>
					),
					h5: ({children}) => (
						<h5
							className={clsx(
								'text-base',
								'font-bold',
								'md:text-lg',
								headingColor,
								'mb-3',
							)}
						>
							{children}
						</h5>
					),
					h6: ({children}) => (
						<h6
							className={clsx(
								'text-base',
								'font-bold',
								'md:text-lg',
								headingColor,
								'mb-3',
							)}
						>
							{children}
						</h6>
					),
				},
				marks: {
					link: ({value, text}: PortableTextMarkComponentProps<LinkSchema>) => (
						<InlineLink inlineLink={value} text={text} isDraft={isDraft} />
					),
					linkAnnotation: () => null,
					strong: ({children}) => (
						<strong className="font-din-alternate-bold">{children}</strong>
					),
				},
				list: {
					bullet: ({children}) => (
						<ul className={clsx('my-8', 'max-w-xl', 'space-y-8', 'text-gray-600')}>
							{children}
						</ul>
					),
					number: ({children}) => (
						<ol className={clsx('my-8', 'max-w-xl', 'space-y-8', 'text-gray-600')}>
							{children}
						</ol>
					),
				},
				listItem: {
					number: ({children, index}) => (
						<li className={clsx('flex', 'gap-x-3')}>
							<NumberedListIcon index={index} />
							<span>{children}</span>
						</li>
					),
					bullet: ({children}) => (
						<li className={clsx('flex', 'gap-x-3')}>
							<CheckCircleIcon
								className={clsx(
									'mt-1',
									'h-5',
									'w-5',
									'flex-none',
									'text-primary-light',
								)}
								aria-hidden="true"
							/>
							<span>{children}</span>
						</li>
					),
				},
				types: {
					callToActionLine: ({value}: {value: CallToActionLineSchema}) => (
						<CallToActionLine value={value} isDraft={isDraft} />
					),
				},
			}}
		/>
	);
};

const NumberedListIcon = ({index}: {index: number}) => (
	<span className={clsx('flex-none', 'text-primary-light')} aria-hidden="true">
		{index + 1}.
	</span>
);
